// src/pages/patient/Consultation.tsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Alert, Badge, Button, Card, Drawer, Select, Space, Tag, Typography, Spin, notification } from "antd";
import { PhoneOutlined, SettingOutlined, AudioOutlined, SyncOutlined, CheckCircleOutlined, LoadingOutlined, DesktopOutlined, MessageOutlined, VideoCameraOutlined, StopOutlined } from "@ant-design/icons";
import { NeuralLoader } from "../../components/icons/NeuralLoader";

import SocketService from "../../services/socketService";
import { AudioStreamingService } from "../../services/audioStreamingService";
import ConsultChatPanel from "../../components/consult/ConsultChatPanel";
import { useConsultChat } from "../../hooks/consult/useConsultChat";
import { useConsultMediaTelemetry, useRemoteVideoRegistry } from "../../hooks/consult/useConsultMediaTelemetry";
import { useCaptureTelemetry } from "../../hooks/consult/useCaptureTelemetry";
import { SilentMicTransitionReason, SilentMicTransitionState, useSilentMicDetector } from "../../hooks/consult/useSilentMicDetector";
import { uploadPatientChatAttachment } from "../../api/healthcare/Consultation/ConsultChatApi";
import { createPeerMesh, PeerMesh } from "../../utils/consult/peerMesh";
import { getAppointmentFromConsultLink } from "../../api/healthcare/Appointment/AppointmentApi";
import { VideoParticipant } from "../../types/appointments/ConsultTypes";
import { getStableParticipantId } from "../../utils/consult/participantIdentity";
import { acquireReplacement, isGetUserMediaTimeout, listInputDevices, MediaKind, releaseReplaced, Replacement, seedDeviceSelection, swapOntoPeers, watchDeviceList, watchTrackEnded } from "../../utils/consult/localMedia";
import { Appointment } from "../../types/appointments/AppointmentTypes";

const { Text, Title } = Typography;


// A consult that runs long must not lock the patient out mid-session, so the window closes well after start.
const JOIN_WINDOW_BEFORE_MS = 60 * 60 * 1000;
const JOIN_WINDOW_AFTER_MS = 6 * 60 * 60 * 1000;

const MIC_CONSTRAINTS: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
// talk accepts one audio_silent per 2 s per socket, so a transition inside that gap is sent when the gap ends rather than dropped.
const MIC_SILENT_REPORT_GAP_MS = 2_000;
const SWITCH_CAMERA_CONSTRAINTS: MediaTrackConstraints = {
	width: { min: 640, ideal: 1280, max: 1920 },
	height: { min: 480, ideal: 720, max: 1080 },
	aspectRatio: { ideal: 16 / 9 },
};
// A phone's system default can be the back camera, so every default-camera request asks for the front one.
const DEFAULT_CAMERA_CONSTRAINTS: MediaTrackConstraints = {
	facingMode: { ideal: "user" },
	width: { min: 640, ideal: 1280, max: 1920 },
	height: { min: 480, ideal: 720, max: 1080 },
	frameRate: { ideal: 30, max: 30 },
	aspectRatio: { ideal: 16 / 9 },
};

// ─── Standalone component: a single remote participant's video tile ────────────
interface RemoteVideoTileProps {
	socketId: string;
	stream: MediaStream;
	label?: string;
	isScreenShare?: boolean;
	letterbox?: boolean;
	registerVideo?: (socketId: string, element: HTMLVideoElement | null) => void;
}

const RemoteVideoTile: React.FC<RemoteVideoTileProps> = ({ socketId, stream, label, isScreenShare, letterbox, registerVideo }) => {
	const ref = useRef<HTMLVideoElement | null>(null);
	const setVideo = useCallback(
		(element: HTMLVideoElement | null) => {
			ref.current = element;
			registerVideo?.(socketId, element);
		},
		[socketId, registerVideo],
	);
	useEffect(() => {
		if (ref.current && stream) ref.current.srcObject = stream;
	}, [stream]);
	return (
		<div style={{ position: "relative", background: "#0d0d0d", borderRadius: 8, overflow: "hidden", height: "100%", width: "100%", minHeight: 80 }}>
			<video ref={setVideo} data-testid="consultation-remote-video" autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: isScreenShare || letterbox ? "contain" : "cover", display: "block" }} />
			{label && (
				<div style={{ position: "absolute", bottom: 8, left: 8, padding: "3px 10px", background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", color: "#fff", fontSize: 11, fontWeight: 500, borderRadius: 20, userSelect: "none" as const, letterSpacing: "0.3px" }}>
					{label}
				</div>
			)}
		</div>
	);
};
// ─────────────────────────────────────────────────────────────────────────────


const PatientConsultation: React.FC = () => {
	const { roomId } = useParams();

	// Video/WebRTC refs – now supporting multiple remote participants (full mesh)
	const localVideoRef = useRef<HTMLVideoElement | null>(null);
	// The per-peer WebRTC state machine — one negotiation core shared with the practitioner page
	// (utils/consult/peerMesh): perfect-negotiation glare handling, candidate queueing, ICE-restart
	// renegotiation and the failed-connection watchdog all live there, created per room in init.
	const meshRef = useRef<PeerMesh | null>(null);
	const localStreamRef = useRef<MediaStream | null>(null);
	const mySocketIdRef = useRef<string>("");
	// Unique ID for this patient's browser session — stable across reconnects AND across a
	// mid-consult refresh (it is persisted per room in sessionStorage), but different for
	// every patient tab even when they share the same roomId link.
	const sessionIdRef = useRef<string>(getStableParticipantId(roomId));

	// Multi-participant remote streams state (socketId → MediaStream)
	const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
	const [videoParticipants, setVideoParticipants] = useState<VideoParticipant[]>([]);

	// Socket service ref
	const socketServiceRef = useRef<SocketService | null>(null);
	const audioStreamingServiceRef = useRef<AudioStreamingService | null>(null);

	// Component state
	const [consultationStarted, setConsultationStarted] = useState<boolean>(false);
	const [consultationEnded, setConsultationEnded] = useState<boolean>(false);
	const [removed, setRemoved] = useState<boolean>(false);
	const [validationState, setValidationState] = useState<"checking" | "valid" | "invalid">("checking");
	const [validationMessage, setValidationMessage] = useState<string>("");
	// Waiting room state — patient waits here until admitted by practitioner
	const [inWaitingRoom, setInWaitingRoom] = useState<boolean>(false);
	const [waitingRoomMessage, setWaitingRoomMessage] = useState<string>("Waiting for your practitioner...");
	const admittedRef = useRef<boolean>(false);

	const [deviceSettingsVisible, setDeviceSettingsVisible] = useState<boolean>(false);
	const [cameras, setCameras] = useState<any[]>([]);
	const [microphones, setMicrophones] = useState<any[]>([]);
	const [selectedCamera, setSelectedCamera] = useState<string>("");
	const [selectedMicrophone, setSelectedMicrophone] = useState<string>("");
	const [voiceActive, setVoiceActive] = useState<boolean>(false);
	const [connecting, setConnecting] = useState<boolean>(true);
	const [reconnecting, setReconnecting] = useState<boolean>(false);
	const [waitingForDoctor, setWaitingForDoctor] = useState<boolean>(true);
	const [screenSharerId, setScreenSharerId] = useState<string | null>(null);
	const doctorScreenSharing = screenSharerId !== null;
	const hideLocalVideo = doctorScreenSharing;
	const [connectionState, setConnectionState] = useState<string>("new");
	const [videoReady, setVideoReady] = useState<boolean>(false);
	const [switchingDevices, setSwitchingDevices] = useState<boolean>(false);
	const switchingDevicesRef = useRef<boolean>(false);
	const mountedRef = useRef<boolean>(true);
	const unwatchLocalTracksRef = useRef<() => void>(() => {});
	const recoveringDeviceRef = useRef<boolean>(false);
	const recheckLostDeviceRef = useRef<boolean>(false);

	// The reactive twin of admittedRef — a ref cannot gate a hook. Admission is the ONLY chat gate.
	const [isAdmitted, setIsAdmitted] = useState<boolean>(false);
	const [chatOpen, setChatOpen] = useState<boolean>(false);
	// Phone-first: below 768px the chat is a bottom sheet and remote tiles stack letterboxed so no frame is cropped.
	const [isNarrow, setIsNarrow] = useState<boolean>(() => typeof window !== "undefined" && window.innerWidth < 768);

	const {
		messages: chatMessages,
		sendMessage: sendChatMessage,
		sendAttachment: sendChatAttachment,
		unreadCount: chatUnread,
		markRead: markChatRead,
		error: chatError,
		dismissError: dismissChatError,
	} = useConsultChat({
		roomId: roomId ?? "",
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		enabled: isAdmitted,
		// No senderName: talk forces it null for a patient socket, so a patient cannot render as staff.
		selfRole: "patient",
		// The whole URL, matching getAppointmentFromConsultLink below — healthcare reduces either form to its last segment.
		uploadAttachment: (file: File) => uploadPatientChatAttachment(window.location.href, file),
	});

	const { registerRemoteVideo, getRemoteVideo } = useRemoteVideoRegistry();
	const { noteConnectionState } = useConsultMediaTelemetry({
		enabled: isAdmitted && !consultationEnded && !removed,
		roomId: roomId ?? "",
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		getMesh: () => meshRef.current,
		getLocalStream: () => localStreamRef.current,
		getRemoteVideo,
	});
	useCaptureTelemetry({
		enabled: isAdmitted && !consultationEnded && !removed,
		roomId: roomId ?? "",
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		getCapture: () => audioStreamingServiceRef.current,
	});

	const micSilenceReportRef = useRef<{ latest: { state: SilentMicTransitionState; reason: SilentMicTransitionReason } | null; sent: SilentMicTransitionState; sentAt: number; timer: ReturnType<typeof setTimeout> | null }>({
		latest: null,
		sent: "left",
		sentAt: 0,
		timer: null,
	});
	const sendMicSilence = useCallback(() => {
		const report = micSilenceReportRef.current;
		report.timer = null;
		const latest = report.latest;
		if (!latest || latest.state === report.sent) return;
		const socket: any = socketServiceRef.current?.getSocket();
		if (!socket?.connected) return;
		socket.emit("consultTelemetry", { roomId, v: 1, kind: "audio_silent", state: latest.state, reason: latest.reason });
		report.sent = latest.state;
		report.sentAt = Date.now();
	}, [roomId]);
	const reportMicSilence = useCallback(
		(state: SilentMicTransitionState, reason: SilentMicTransitionReason) => {
			const report = micSilenceReportRef.current;
			report.latest = { state, reason };
			if (report.timer) return;
			const wait = report.sentAt + MIC_SILENT_REPORT_GAP_MS - Date.now();
			if (wait > 0) report.timer = setTimeout(sendMicSilence, wait);
			else sendMicSilence();
		},
		[sendMicSilence],
	);
	const { silent: micSilent } = useSilentMicDetector({
		enabled: isAdmitted && !consultationEnded && !removed,
		// Optional call: the capture mocks in older page suites predate getInputLevel.
		getLevel: () => audioStreamingServiceRef.current?.getInputLevel?.() ?? null,
		getTrack: () => localStreamRef.current?.getAudioTracks()[0] ?? null,
		onTransition: reportMicSilence,
	});
	// Declared after the detector, whose unmount cleanup can still report a final 'left'.
	useEffect(
		() => () => {
			const { timer } = micSilenceReportRef.current;
			if (timer) clearTimeout(timer);
		},
		[],
	);

	useEffect(() => {
		const onResize = () => setIsNarrow(window.innerWidth < 768);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// The panel names a message by its senderName, and talk sends none for a patient — so "Patient"
	// is what the patient would read against their own words.
	const chatThread = useMemo(() => chatMessages.map((message) => (message.senderRole === "patient" ? { ...message, senderName: "You" } : message)), [chatMessages]);

	// Open means read, including for arrivals while it is open.
	useEffect(() => {
		if (chatOpen && chatUnread > 0) markChatRead();
	}, [chatOpen, chatUnread, markChatRead]);

	// Start streaming microphone audio for transcription once the consult is live. Guarded so the
	// several paths that can signal liveness start it exactly once.
	//
	// Triggered on ADMISSION (and on a reconnect while admitted), with the first inbound offer
	// kept as a fallback — it used to be offer-ONLY, which tied the patient's transcription to
	// WebRTC health: in the incident consultation every page load whose video negotiation failed
	// transcribed nothing for its whole life, with the socket and microphone both fine.

	// One log per state transition — the watchdog below re-attempts every 15s and must not narrate each tick.
	const captureHealthRef = useRef<string>("");

	const startAudioStreaming = useCallback(async (announce = true) => {
		if (!localStreamRef.current || audioStreamingServiceRef.current) return;
		const audioStream = new MediaStream(localStreamRef.current.getAudioTracks());
		const audioService = new AudioStreamingService({
			stream: audioStream,
			roomId: roomId || "",
			participantId: sessionIdRef.current,
			roomType: "online",
			onVoiceDetected: setVoiceActive,
			maxChunkSize: 10,
		});
		// The ref is the once-guard, so it is set BEFORE the awaited init: admission and the
		// first offer can fire in the same tick and must not each build a capture pipeline.
		audioStreamingServiceRef.current = audioService;
		if (!(await audioService.init())) {
			audioService.stop();
			// A dead service left in the ref would block every later trigger for the whole consult.
			if (audioStreamingServiceRef.current === audioService) audioStreamingServiceRef.current = null;
			if (captureHealthRef.current !== "init_failed") {
				console.warn("consult.audio.init_failed");
				captureHealthRef.current = "init_failed";
			}
			return;
		}
		audioService.start();
		captureHealthRef.current = "";
		// Only on a genuinely live pipeline — otherwise the patient is told their microphone is on when it is not.
		if (announce) notification.info({ message: "Consultation Started", description: "Your microphone is now active for transcription", duration: 3 });
	}, [roomId]);

	// Route incoming signals into the mesh (utils/consult/peerMesh owns offer/answer/candidate
	// handling, glare and recovery — shared with the practitioner page so the two ends cannot drift
	// apart again). An inbound offer also means a practitioner is negotiating with us — the consult
	// is starting — which is what the old inline handler keyed its UI state and audio start off.
	const handleVideoSignal = useCallback(
		(data: any) => {
			if (data?.type === "offer") {
				setConsultationStarted(true);
				setWaitingForDoctor(false);
				startAudioStreaming();
			}
			return meshRef.current?.handleVideoSignal(data);
		},
		[startAudioStreaming],
	);

	// Validate that the patient has a valid upcoming appointment before allowing access.
	// ⚠️ `roomId` IS the consultation link — it is the credential this page authenticates with
	// (`initialize(..., { consultationLink: roomId })`, no JWT). It must never be logged.
	useEffect(() => {
		if (!roomId) {
			setValidationState("invalid");
			setValidationMessage("Invalid consultation link.");
			return;
		}
		async function validate() {
			try {
				const { data } = await getAppointmentFromConsultLink(window.location.href);
				let apt:Appointment = data.appointment;
				let ready = data.ready;
				if (!ready) {
					setValidationState("invalid");
					setValidationMessage("No appointment found for this consultation link.");
					return;
				}
				if (apt.status === "Completed") {
					setValidationState("invalid");
					setValidationMessage("This consultation has already been completed.");
					return;
				}
				const now = Date.now();
				const startTime = new Date(apt.start_time).getTime();
				// Joining opens 1 hour before start and closes 6 hours after start
				if (startTime > now + JOIN_WINDOW_BEFORE_MS) {
					const minutesUntil = Math.round((startTime - now) / 60000);
					setValidationState("invalid");
					setValidationMessage(`Your appointment is in ${minutesUntil} minutes. Please return closer to your appointment time.`);
					return;
				}
				if (startTime < now - JOIN_WINDOW_AFTER_MS) {
					// Ids only — `roomId` is the bearer credential this page authenticates with.
					console.warn("consult.join_window.refused", { appointment_id: apt.id });
					setValidationState("invalid");
					setValidationMessage("This appointment time has passed. Please contact your healthcare provider.");
					return;
				}
				setValidationState("valid");
			} catch {
				setValidationState("invalid");
				setValidationMessage("No appointment found for this consultation link.");
				return;
			}
		}
		validate();
	}, [roomId]);

	const attachLocalPreview = useCallback((stream: MediaStream | null) => {
		if (localVideoRef.current) localVideoRef.current.srcObject = stream;
	}, []);

	// Both local <video>s remount on layout and screen-share toggles, so attach on mount — an effect misses some remounts.
	const setLocalVideoElement = useCallback((element: HTMLVideoElement | null) => {
		localVideoRef.current = element;
		if (element) element.srcObject = localStreamRef.current;
	}, []);

	// Initialize component
	useEffect(() => {
		let isComponentMounted = true;

		// Only proceed once the appointment has been validated
		if (validationState !== "valid") return;

		async function init() {
			setConnecting(true);

			try {
				// Initialize socket service — patients authenticate via consultation link (no JWT)
				socketServiceRef.current = SocketService.getInstance();
				const socket: any = socketServiceRef.current.initialize(
					roomId || "",
					"online",
					{ consultationLink: roomId || "" }
				);

				// One mesh per room connection. RTCConfiguration (ICE servers included) comes from
				// config inside the mesh — see utils/consult/iceServers.
				meshRef.current?.closeAll();
				meshRef.current = createPeerMesh({
					roomId: roomId || "",
					logTag: "Patient",
					// `as any`: socket.io-client v2's `typeof Socket` typing does not expose emit
					// structurally — the same reason every socket in this file is `const socket: any`.
					getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
					getMySocketId: () => mySocketIdRef.current,
					getLocalStream: () => localStreamRef.current,
					onRemoteStream: (peerSocketId, stream) => {
						if (!isComponentMounted) return;
						setRemoteStreams((prev) => new Map(prev).set(peerSocketId, stream));
						setWaitingForDoctor(false);
					},
					onPeerClosed: (peerSocketId) => {
						if (!isComponentMounted) return;
						setRemoteStreams((prev) => {
							const m = new Map(prev);
							m.delete(peerSocketId);
							return m;
						});
					},
					onConnectionStateChange: (peerSocketId, state) => {
						if (!isComponentMounted) return;
						setConnectionState(state);
						noteConnectionState(peerSocketId, state);
						const si: any = socketServiceRef.current?.getSocket();
						if (si) si.emit("connectionStateChange", { roomId, connectionState: state });

						if (state === "connected") {
							setReconnecting(false);
							setWaitingForDoctor(false);
							notification.success({ message: "Connected", description: "Video connection established", duration: 3 });
						} else if (state === "failed") {
							// The mesh owns the actual recovery (ICE restart, then teardown + requestOffers).
							setReconnecting(true);
							notification.error({ message: "Connection Failed", description: "Attempting to reestablish...", duration: 5 });
						} else if (state === "disconnected") {
							setReconnecting(true);
							notification.warning({ message: "Connection Interrupted", description: "Attempting to reconnect...", duration: 5 });
						}
					},
				});

				// Set up socket event listeners
				socket.on("videoSignal", handleVideoSignal);

				socket.on("offerRequested", async (data: any) => {
					const { requesterSocketId } = data;
					console.log(`📹 [Patient] offerRequested from ${requesterSocketId}`);
					await meshRef.current?.handleOfferRequested(requesterSocketId);
				});

				// Waiting room events
				socket.on("waitingRoomJoined", (data: any) => {
					setInWaitingRoom(true);
					setWaitingRoomMessage(data.message || "Waiting for your practitioner...");
				});

				socket.on("admittedToConsultation", (data: any) => {
					admittedRef.current = true;
					setIsAdmitted(true);
					setInWaitingRoom(false);
					setWaitingRoomMessage("");
					// Before joinVideoRoom: talk admits a reconnecting socket from its handshake alone.
					if (typeof data?.admissionToken === "string") socketServiceRef.current?.setAdmissionToken(data.admissionToken);
					// Now join the video room properly
					socket.emit("joinVideoRoom", { roomId, participantType: "patient", userId: sessionIdRef.current, displayName: "Patient", audioParticipantId: sessionIdRef.current });
					socket.emit("videoReady", { roomId });
					// Admission is the consent gate — transcription capture starts here, not on
					// WebRTC negotiation succeeding (see startAudioStreaming).
					startAudioStreaming();
					notification.success({ message: "Admitted", description: data.message || "Your practitioner is ready.", duration: 4 });
				});

				socket.on("waitingRoomDismissed", (data: any) => {
					setInWaitingRoom(false);
					setValidationState("invalid");
					setValidationMessage(data.reason || "The practitioner is not available at this time.");
				});

				// talk refused joinVideoRoom (admission revoked or expired); the camera stays live so re-admission is instant.
				socket.on("notAdmitted", () => {
					if (!admittedRef.current) return;
					console.warn("consult.admission.refused");
					admittedRef.current = false;
					setIsAdmitted(false);
					socketServiceRef.current?.setAdmissionToken(null);
					if (audioStreamingServiceRef.current) {
						audioStreamingServiceRef.current.stop();
						audioStreamingServiceRef.current = null;
					}
					setVoiceActive(false);
					meshRef.current?.closeAll();
					setRemoteStreams(new Map());
					// Stops the capture watchdog, which would otherwise restart the capture just stopped.
					setConsultationStarted(false);
					setWaitingForDoctor(true);
					socket.emit("joinWaitingRoom", { roomId, userId: sessionIdRef.current, displayName: "Patient" });
					setInWaitingRoom(true);
					setWaitingRoomMessage("Please wait — your practitioner will admit you.");
					notification.info({ message: "Waiting Room", description: "Please wait — your practitioner will admit you", duration: 4 });
				});

				// Terminal: talk cannot disconnect us (socketService reconnects on a server disconnect), so the page does.
				socket.on("removedFromConsultation", () => {
					console.warn("consult.participant.removed");
					admittedRef.current = false;
					setIsAdmitted(false);
					setChatOpen(false);
					if (audioStreamingServiceRef.current) {
						audioStreamingServiceRef.current.stop();
						audioStreamingServiceRef.current = null;
					}
					if (localStreamRef.current) {
						localStreamRef.current.getTracks().forEach((track) => track.stop());
						localStreamRef.current = null;
					}
					meshRef.current?.closeAll();
					setRemoteStreams(new Map());
					attachLocalPreview(null);
					// disconnect() also drops the admission token.
					socketServiceRef.current?.disconnect();
					setInWaitingRoom(false);
					setReconnecting(false);
					setConsultationStarted(false);
					setVideoReady(false);
					setVoiceActive(false);
					setRemoved(true);
				});

				socket.on("consultationStarted", () => {
					console.log("✓ Consultation started signal received");
					setConsultationStarted(true);
				});

				socket.on("consultationEnded", () => {
					console.log("🔴 [PATIENT] Consultation ended");

					// Stop all local media streams
					if (localStreamRef.current) {
						localStreamRef.current.getTracks().forEach((track) => track.stop());
						localStreamRef.current = null;
					}

					// Stop audio streaming if active
					if (audioStreamingServiceRef.current) {
						audioStreamingServiceRef.current.stop();
						audioStreamingServiceRef.current = null;
					}

					// Close all peer connections
					meshRef.current?.closeAll();
					setRemoteStreams(new Map());

					// Clear local video
					attachLocalPreview(null);

					setConsultationStarted(false);
					setConsultationEnded(true);
					setVideoReady(false);
					setVoiceActive(false);
				});

				socket.on("participantJoined", (data: any) => {
					setVideoParticipants((prev) => {
						const exists = prev.some((p) => p.socketId === data.socketId);
						if (exists) return prev.map((p) => (p.socketId === data.socketId ? { ...p, ...data } : p));
						return [...prev, data];
					});
					if (data.participantType === "practitioner") {
						notification.info({ message: "Doctor Joined", description: "Your doctor has joined the consultation", duration: 3 });
						setWaitingForDoctor(false);
					}
				});

				socket.on("videoParticipantListUpdate", (data: any) => {
					if (!isComponentMounted) return;
					setVideoParticipants(data.participants || []);
					const hasPractitioner = data.participants?.some((p: any) => p.participantType.toLowerCase() === "practitioner");
					if (hasPractitioner) setWaitingForDoctor(false);
				});

				socket.on("videoRoomState", (data: any) => {
					if (!isComponentMounted) return;
					if (data.yourSocketId) {
						mySocketIdRef.current = data.yourSocketId;
						meshRef.current?.resetIfSocketIdChanged();
					}
					setVideoParticipants(data.participants || []);
					const hasPractitioner = data.participants?.some((p: any) => p.participantType.toLowerCase() === "practitioner");
					if (hasPractitioner) setWaitingForDoctor(false);
					if (data.consultationActive) { setConsultationStarted(true); setWaitingForDoctor(false); }
					setScreenSharerId(data.screenSharing || null);

					// No offer from here: talk names the patient the offerer of every patient/practitioner pair (server.js offererFor). The old dual-offer only manufactured glare — 14ygtkj6cwe.
				});

				socket.on("consultationAlreadyActive", (data: any) => {
					if (!isComponentMounted) return;
					setConsultationStarted(true);
					setWaitingForDoctor(false);
					notification.info({
						message: "Consultation Active",
						description: "You've joined an active consultation",
						duration: 3,
					});
				});

				socket.on("remoteScreenShareStarted", (data: any) => {
					console.log("Doctor started screen sharing");
					// A sharer-less event must still enter share state; "unknown" matches no socket id.
					setScreenSharerId(data?.sharerSocketId || "unknown");
					notification.info({
						message: "Screen Sharing",
						description: "Your doctor is now sharing their screen",
						duration: 3,
						icon: <DesktopOutlined style={{ color: "#1890ff" }} />,
					});
				});

				socket.on("remoteScreenShareStopped", (data: any) => {
					console.log("Doctor stopped screen sharing");
					setScreenSharerId(null);
					notification.info({
						message: "Screen Sharing Ended",
						description: "Your doctor has stopped sharing their screen",
						duration: 3,
					});
				});

				// Set connection status handlers
				socket.on("connect", () => {
					console.log("✓ Socket connected");
					mySocketIdRef.current = socket.id || "";
					// A reconnect means a NEW socket id: every existing RTCPeerConnection is the stale
					// half of a pair the practitioner is about to close (participantLeft) and re-offer
					// to the new id, so rebuild from zero rather than reuse.
					meshRef.current?.resetIfSocketIdChanged();
					setConnecting(false);
					setReconnecting(false);

					(window as any).testEndConsultation = () => { socket.emit("testEndConsultation", roomId); };

					// If media is already ready (socket reconnected mid-session), re-join appropriately
					if (localStreamRef.current) {
						if (admittedRef.current) {
							// Already admitted — rejoin video directly
							socket.emit("joinVideoRoom", { roomId, participantType: "patient", userId: sessionIdRef.current, displayName: "Patient", audioParticipantId: sessionIdRef.current });
							socket.emit("videoReady", { roomId });
							// Capture is socket-agnostic and usually still running (segments queue in
							// socketService while the socket is down); this is the no-op-when-running
							// re-arm for the load where admission raced the audio start.
							startAudioStreaming();
							// A new socket is a new tile to the practitioner, so re-announce a silence that is still current.
							const silence = micSilenceReportRef.current;
							if (silence.latest?.state === "entered") {
								socket.emit("consultTelemetry", { roomId, v: 1, kind: "audio_silent", state: "entered", reason: silence.latest.reason });
								silence.sent = "entered";
								silence.sentAt = Date.now();
							}
						} else {
							// Not yet admitted — re-enter waiting room
							socket.emit("joinWaitingRoom", { roomId, userId: sessionIdRef.current, displayName: "Patient" });
						}
					}
				});

				socket.on("disconnect", (reason: string) => {
					console.log("Socket disconnected:", reason);

					if (reason === "transport close" || reason === "transport error") {
						setReconnecting(true);

						notification.warning({
							message: "Connection Issue",
							description: "Connection interrupted. Attempting to reconnect...",
							duration: 3,
						});
					}
				});

				socket.on("participantLeft", (data: any) => {
					const leftSocketId: string = data.socketId;

					// Close that peer's connection (the mesh also drops its buffered candidates and
					// removes the stream tile via onPeerClosed)
					meshRef.current?.closePeer(leftSocketId);
					setVideoParticipants((prev) => prev.filter((p) => p.socketId !== leftSocketId));
					// talk clears a departed sharer's slot without emitting remoteScreenShareStopped.
					setScreenSharerId((prev) => (prev === leftSocketId ? null : prev));

					if (data.participantType === "practitioner") {
						notification.warning({ message: "Doctor Left", description: "Your doctor has left the consultation", duration: 5 });
						// Only set waitingForDoctor if no practitioners remain
						const remainingPractitioners: string[] = [];
						meshRef.current?.forEachPc((_pc, sid) => {
							const p = videoParticipants.find((vp) => vp.socketId === sid);
							if (p?.participantType === "practitioner") remainingPractitioners.push(sid);
						});
						if (remainingPractitioners.length === 0) {
							setWaitingForDoctor(true);
							setConsultationStarted(false);
						}
					}
				});

				// Get user media
				const stream = await navigator.mediaDevices.getUserMedia({ video: DEFAULT_CAMERA_CONSTRAINTS, audio: MIC_CONSTRAINTS });

				adoptLocalStream(stream);
				attachLocalPreview(stream);
				console.log("✓ Got local media stream");
				setVideoReady(true);

				// Any peer connection created before media resolved answered receive-only; addTrack
				// here reuses its reserved transceivers and renegotiates to two-way.
				meshRef.current?.attachLocalTracksToAll();

				// Enumerate available devices
				await enumerateDevices();

				// Join the waiting room — practitioner must admit the patient before video starts
				socket.emit("joinWaitingRoom", { roomId, userId: sessionIdRef.current, displayName: "Patient" });
				setInWaitingRoom(true);
				setWaitingRoomMessage("You have joined the waiting room. Your practitioner will admit you shortly.");

				setConnecting(false);
			} catch (err) {
				console.error("Initialization error:", err);
				setConnecting(false);

				notification.error({
					message: "Setup Error",
					description: "Could not access camera or microphone. Please check your permissions.",
					duration: 7,
				});
			}
		}

		init();

		// Cleanup on unmount
		return () => {
			isComponentMounted = false;
			console.log("📹 [Patient] Cleaning up consultation");

			// Stop audio streaming
			if (audioStreamingServiceRef.current) {
				audioStreamingServiceRef.current.stop();
				audioStreamingServiceRef.current = null;
			}

			// Stop local media
			if (localStreamRef.current) {
				localStreamRef.current.getTracks().forEach((track) => track.stop());
				localStreamRef.current = null;
			}

			// Close all peer connections (buffered candidates and recovery timers go with them)
			meshRef.current?.closeAll();
			meshRef.current = null;

			// Disconnect socket
			if (socketServiceRef.current) {
				const socket: any = socketServiceRef.current.getSocket();
				if (socket && roomId) {
					socket.emit("leaveVideoRoom", { roomId });
				}
				socketServiceRef.current.disconnect();
				socketServiceRef.current = null;
			}
		};
	}, [roomId, handleVideoSignal, startAudioStreaming, validationState, attachLocalPreview, noteConnectionState]);

	// Keep the room's activity clock alive while the patient waits to be admitted.
	//
	// talk reaps any room with no inbound socket event for 5 minutes (server.js:1014, :1047) and
	// this page emits NOTHING between `joinWaitingRoom` and admission — every other emit here is on
	// a video-signal path that only runs once an offer arrives. So a patient waiting more than five
	// minutes, or a practitioner who opened the room early, had it deleted underneath them: the
	// server emits `roomClosed` (which no client anywhere listens for) and `leave()`s every socket,
	// after which `joinWaitingRoom` finds no room and never notifies the practitioner. Both sides
	// then wait forever, each believing the other has not arrived.
	//
	// `heartbeat` is the server's own handler (server.js:2311) and is deliberately ungated — it
	// emits nothing and returns nothing, it only stamps the clock.
	useEffect(() => {
		if (!roomId) return;
		const timer = setInterval(() => {
			const socket: any = socketServiceRef.current?.getSocket();
			if (socket?.connected) socket.emit("heartbeat", roomId);
		}, 60_000);
		return () => clearInterval(timer);
	}, [roomId]);

	// Capture is armed from an admission event or an inbound offer, neither of which is a user gesture —
	// so a transient init failure or a suspended AudioContext otherwise costs the whole consult its transcript.
	useEffect(() => {
		if (!consultationStarted) return;
		const timer = setInterval(() => {
			if (!localStreamRef.current) return;
			const service = audioStreamingServiceRef.current;
			if (!service) {
				startAudioStreaming();
				return;
			}
			if (service.isCapturing) {
				captureHealthRef.current = "";
				return;
			}
			if (captureHealthRef.current !== "stalled") {
				console.warn("consult.audio.capture_stalled");
				captureHealthRef.current = "stalled";
			}
			service.stop();
			if (audioStreamingServiceRef.current === service) audioStreamingServiceRef.current = null;
			startAudioStreaming();
		}, 15_000);
		return () => clearInterval(timer);
	}, [consultationStarted, startAudioStreaming]);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			unwatchLocalTracksRef.current();
		};
	}, []);

	// A still-listed selection is kept; `reseed` kinds follow the device actually in use.
	const enumerateDevices = async (reseed: MediaKind[] = []) => {
		try {
			const { cameras: videoDevices, microphones: audioDevices } = await listInputDevices();

			setCameras(videoDevices);
			setMicrophones(audioDevices);

			// Seed from the device getUserMedia actually opened, so an untouched selection reads as unchanged.
			const stream = localStreamRef.current;
			if (videoDevices.length > 0) setSelectedCamera((prev) => seedDeviceSelection(videoDevices, stream?.getVideoTracks()[0], reseed.includes("video") ? undefined : prev));
			if (audioDevices.length > 0) setSelectedMicrophone((prev) => seedDeviceSelection(audioDevices, stream?.getAudioTracks()[0], reseed.includes("audio") ? undefined : prev));

			console.log(`✓ Found ${audioDevices.length} microphones and ${videoDevices.length} cameras`);
		} catch (err) {
			console.warn("consult.devices.enumerate_failed", { errorName: (err as { name?: string })?.name ?? "unknown" });

			notification.error({
				message: "Device Error",
				description: "Could not detect available cameras and microphones",
				duration: 5,
			});
		}
	};

	// iOS fires devicechange when a call takes the mic and can rotate deviceIds, so this only refreshes the list — it never switches.
	useEffect(() => {
		if (!videoReady) return;
		return watchDeviceList(() => {
			enumerateDevices();
		});
	}, [videoReady]);

	const adoptLocalStream = (stream: MediaStream) => {
		localStreamRef.current = stream;
		unwatchLocalTracksRef.current();
		unwatchLocalTracksRef.current = watchTrackEnded(stream, handleLocalTrackEnded);
	};

	// Swap first, stop last: a peer is never left sending an ended track.
	const commitReplacement = async (oldStream: MediaStream | null, replacement: Replacement) => {
		const { stream, changedAudio } = replacement;
		adoptLocalStream(stream);
		attachLocalPreview(stream);

		const swapped = swapOntoPeers((visit) => meshRef.current?.forEachPc(visit), stream);

		if (changedAudio && audioStreamingServiceRef.current) {
			audioStreamingServiceRef.current.stop();
			audioStreamingServiceRef.current = null;
			startAudioStreaming(false);
		}

		if (!(await swapped)) console.warn("consult.device_change.replace_failed");
		releaseReplaced(oldStream, stream);
	};

	const handleLocalTrackEnded = (track: MediaStreamTrack) => {
		// Our own stop() (a swap, consult end, removal, unmount) has already dropped the track from the live stream.
		if (!mountedRef.current || !localStreamRef.current?.getTracks().includes(track)) return;
		console.warn("consult.device_lost", { kind: track.kind });
		recoverLostDevices();
	};

	const recoverLostDevices = async () => {
		if (recoveringDeviceRef.current) {
			recheckLostDeviceRef.current = true;
			return;
		}
		recoveringDeviceRef.current = true;
		try {
			do {
				recheckLostDeviceRef.current = false;
				await fallBackToDefaultDevices();
			} while (recheckLostDeviceRef.current && mountedRef.current);
		} finally {
			recoveringDeviceRef.current = false;
		}
	};

	const fallBackToDefaultDevices = async () => {
		const oldStream = localStreamRef.current;
		const lost = (oldStream?.getTracks() ?? []).filter((track) => track.readyState === "ended").map((track) => track.kind as MediaKind);
		if (!oldStream || lost.length === 0) return;
		const device = lost.length > 1 ? "camera and microphone" : lost[0] === "audio" ? "microphone" : "camera";
		const title = device.charAt(0).toUpperCase() + device.slice(1);
		const was = lost.length > 1 ? "were" : "was";

		let replacement: Replacement | null;
		try {
			replacement = await acquireReplacement({
				current: oldStream,
				cameraId: lost.includes("video") ? null : undefined,
				micId: lost.includes("audio") ? null : undefined,
				videoConstraints: DEFAULT_CAMERA_CONSTRAINTS,
				audioConstraints: MIC_CONSTRAINTS,
			});
		} catch (err) {
			if (!mountedRef.current || localStreamRef.current !== oldStream) return;
			console.warn("consult.device_lost.fallback_failed", { errorName: (err as { name?: string })?.name ?? "unknown" });
			notification.error({
				message: `${title} unavailable`,
				description: `Your ${device} ${was} disconnected and the default could not be opened. Open Settings to choose another device.`,
				duration: 8,
			});
			return;
		}
		if (!replacement) return;
		// The consult ended, the page unmounted or an Apply won while the default was opening.
		if (!mountedRef.current || localStreamRef.current !== oldStream) {
			releaseReplaced(replacement.stream, oldStream);
			return;
		}

		await commitReplacement(oldStream, replacement);
		notification.warning({
			message: `${title} disconnected`,
			description: `Your ${device} ${was} disconnected — switched to your default ${device}`,
			duration: 5,
		});
		enumerateDevices(lost);
	};

	// Acquire-then-swap: a failed getUserMedia must leave the current camera and mic live on every peer and in transcription.
	const handleDeviceChange = async () => {
		if (!selectedCamera || !selectedMicrophone || switchingDevicesRef.current) return;
		switchingDevicesRef.current = true;
		setSwitchingDevices(true);
		try {
			await switchDevices();
		} finally {
			switchingDevicesRef.current = false;
			setSwitchingDevices(false);
		}
	};

	const switchDevices = async () => {
		const oldStream = localStreamRef.current;
		let replacement: Replacement | null;
		try {
			replacement = await acquireReplacement({
				current: oldStream,
				cameraId: selectedCamera,
				micId: selectedMicrophone,
				videoConstraints: SWITCH_CAMERA_CONSTRAINTS,
				audioConstraints: MIC_CONSTRAINTS,
			});
		} catch (err) {
			console.warn("consult.device_change.failed", { error: (err as { name?: string })?.name ?? "unknown" });
			// A hung device times out 15 s later, by which time the patient may have left the page.
			if (!mountedRef.current) return;
			notification.error({
				message: "Device Change Error",
				description: isGetUserMediaTimeout(err) ? "Your device didn't respond — try again or pick another" : "Failed to switch camera or microphone",
				duration: 5,
			});
			return;
		}
		if (!replacement) {
			setDeviceSettingsVisible(false);
			return;
		}

		// The consult ended, the page unmounted or another switch won while the device was opening.
		if (!mountedRef.current || localStreamRef.current !== oldStream) {
			releaseReplaced(replacement.stream, oldStream);
			return;
		}

		await commitReplacement(oldStream, replacement);

		notification.success({
			message: "Devices Updated",
			description: "Camera and microphone changed successfully",
			duration: 3,
		});
		setDeviceSettingsVisible(false);
	};

	// Leave the call and clean up
	const leaveCall = () => {
		if (window.confirm("Are you sure you want to leave the consultation?")) {
			// Disconnect socket
			if (socketServiceRef.current) {
				const socket: any = socketServiceRef.current.getSocket();
				if (socket && roomId) socket.emit("leaveVideoRoom", { roomId });
				socketServiceRef.current.disconnect();
			}
			// Close all peer connections
			meshRef.current?.closeAll();
			// Stop audio streaming
			if (audioStreamingServiceRef.current) audioStreamingServiceRef.current.stop();
			window.location.href = "/";
		}
	};

	if (validationState === "checking") {
		return (
			<div className="patient-consult-root" style={{ width: "100%", position: "relative" }}>
				<NeuralLoader size="full" text="" fullscreen={true} backgroundColor="black" networkColor="white" />
				<div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
					<Card
						className="rounded-2xl shadow-md border-0"
						style={{ maxWidth: 400, width: "90%", textAlign: "center" }}
						styles={{ body: { padding: "40px 48px" } }}
					>
						<Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 48, color: "var(--primary-color)" }} spin />} />
						<Title level={4} style={{ marginTop: 24, marginBottom: 8, color: "#1f2937" }}>
							Verifying your appointment...
						</Title>
						<Text style={{ color: "#6b7280", fontSize: 14 }}>Please wait while we confirm your access</Text>
					</Card>
				</div>
			</div>
		);
	}

	if (validationState === "invalid") {
		return (
			<div className="patient-consult-root" style={{ width: "100%", background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, overflowY: "auto" }}>
				<Card
					className="rounded-2xl border-0"
					style={{ maxWidth: 500, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
					styles={{ body: { padding: "52px 40px" } }}
				>
					<div style={{ fontSize: 64, marginBottom: 20, color: "#ff4d4f" }}>
						<VideoCameraOutlined />
					</div>
					<Title level={3} style={{ color: "#1f2937", margin: "0 0 12px 0" }}>
						Unable to Join Consultation
					</Title>
					<Text style={{ fontSize: 15, color: "#6b7280", display: "block", marginBottom: 32, lineHeight: 1.6 }}>{validationMessage}</Text>
					<Button type="primary" size="large" style={{ height: 48, padding: "0 32px", fontSize: 16, borderRadius: 8 }} onClick={() => (window.location.href = "/")}>
						Return to Dashboard
					</Button>
				</Card>
			</div>
		);
	}

	return (
		<>
			{/* Mobile-safe viewport fix: dvh accounts for browser chrome on iOS/Android */}
			<style>{`
				.patient-consult-root {
					height: 100vh;
					height: 100dvh;
				}
				@media (max-width: 480px) {
					.consult-settings-label { display: none; }
					.consult-status-tag { max-width: 90px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
				}
				/* Out-specify global.css's !important .ant-btn-default overrides, which paint dark text on this black toolbar. */
				.ant-btn.ant-btn-default.consult-toolbar-btn,
				.ant-btn.ant-btn-default.consult-toolbar-btn:hover,
				.ant-btn.ant-btn-default.consult-toolbar-btn:focus-visible {
					background: rgba(255,255,255,0.08) !important;
					border-color: rgba(255,255,255,0.15) !important;
					color: var(--mg-text-on-primary) !important;
				}
				.ant-btn.ant-btn-default.consult-chat-fab,
				.ant-btn.ant-btn-default.consult-chat-fab:hover,
				.ant-btn.ant-btn-default.consult-chat-fab:focus-visible {
					background: rgba(0,0,0,0.65) !important;
					border-color: rgba(255,255,255,0.25) !important;
					color: var(--mg-text-on-primary) !important;
				}
				.ant-btn.ant-btn-default.consult-toolbar-btn:hover,
				.ant-btn.ant-btn-default.consult-toolbar-btn:focus-visible,
				.ant-btn.ant-btn-default.consult-chat-fab:hover,
				.ant-btn.ant-btn-default.consult-chat-fab:focus-visible {
					border-color: var(--mg-primary) !important;
				}
			`}</style>
		<div
			className="patient-consult-root"
			data-testid="consultation-root"
			style={{
				width: "100%",
				background: "#000",
				display: "flex",
				flexDirection: "column",
				position: "relative",
				overflow: "hidden",
				overscrollBehavior: "none",
				touchAction: "none",
			}}
		>
			{/* Top Toolbar */}
			<div
				style={{
					padding: "8px 12px",
					background: "linear-gradient(to bottom, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 100%)",
					borderBottom: "1px solid rgba(255,255,255,0.06)",
					zIndex: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					backdropFilter: "blur(8px)",
					flexShrink: 0,
					minHeight: 44,
				}}
			>
				<Space size={6} style={{ flexWrap: "nowrap", overflow: "hidden" }}>
					<Button danger icon={<PhoneOutlined />} onClick={leaveCall} size="small" data-testid="consultation-leave-btn" style={{ borderRadius: 6, fontWeight: 500, flexShrink: 0 }}>
						Leave Call
					</Button>

					{/* Connection Status badges — only one visible at a time */}
					{connecting && (
						<Tag color="gold" className="consult-status-tag" style={{ borderRadius: 20, fontSize: 11, lineHeight: "20px", margin: 0 }}>Connecting…</Tag>
					)}
					{voiceActive && !connecting && !reconnecting && (
						<Tag color="success" icon={<AudioOutlined />} className="consult-status-tag" style={{ borderRadius: 20, fontSize: 11, lineHeight: "20px", margin: 0 }}>Speaking</Tag>
					)}
					{videoReady && !waitingForDoctor && !reconnecting && !connecting && (
						<Tag color="success" icon={<CheckCircleOutlined />} className="consult-status-tag" style={{ borderRadius: 20, fontSize: 11, lineHeight: "20px", margin: 0 }}>Connected</Tag>
					)}
					{reconnecting && (
						<Tag color="warning" icon={<SyncOutlined spin />} className="consult-status-tag" style={{ borderRadius: 20, fontSize: 11, lineHeight: "20px", margin: 0 }}>Reconnecting…</Tag>
					)}
					{doctorScreenSharing && (
						<Tag color="processing" icon={<DesktopOutlined />} className="consult-status-tag" style={{ borderRadius: 20, fontSize: 11, lineHeight: "20px", margin: 0 }}>Screen Share</Tag>
					)}
				</Space>

				<Button
					icon={<SettingOutlined />}
					onClick={() => setDeviceSettingsVisible(true)}
					size="small"
					className="consult-toolbar-btn"
					data-testid="consultation-settings-btn"
					style={{ borderRadius: 6, flexShrink: 0 }}
				>
					<span className="consult-settings-label">Settings</span>
				</Button>
			</div>

			{/* Main Video Area */}
			<div style={{ flex: 1, position: "relative", minHeight: 0, overflow: "hidden" }}>
			{micSilent && (
				<div style={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 6 }}>
					<Alert
						type="warning"
						showIcon
						data-testid="consultation-mic-silent-banner"
						message="We can't hear your microphone. Check it isn't muted, or choose another microphone."
						action={
							<Button size="small" data-testid="consultation-mic-silent-choose" onClick={() => setDeviceSettingsVisible(true)}>
								Choose microphone
							</Button>
						}
					/>
				</div>
			)}
			{/* Remote participants grid */}
			<div
				data-testid="consultation-remote-grid"
				style={{
					width: "100%",
					height: "100%",
					display: "grid",
					gridTemplateColumns: isNarrow || remoteStreams.size <= 1 ? "1fr" : remoteStreams.size <= 4 ? "1fr 1fr" : "1fr 1fr 1fr",
					gridAutoRows: isNarrow ? "minmax(0, 1fr)" : undefined,
					gap: 4,
					background: "#111",
				}}
			>
				{Array.from(remoteStreams.entries()).map(([socketId, stream]) => {
					const peer = videoParticipants.find((p) => p.socketId === socketId);
					const label = peer?.displayName || (peer?.participantType === "practitioner" ? "Doctor" : "Participant");
					return <RemoteVideoTile key={socketId} socketId={socketId} stream={stream} label={label} isScreenShare={socketId === screenSharerId} letterbox={isNarrow} registerVideo={registerRemoteVideo} />;
				})}
				{remoteStreams.size === 0 && (
					<video ref={setLocalVideoElement} data-testid="consultation-local-video" autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }} />
				)}
			</div>

			{/* Local preview thumbnail when remote peers are present */}
			{remoteStreams.size > 0 && !hideLocalVideo && (
				<div
					style={{
						position: "absolute",
						bottom: 12,
						right: 12,
						width: "min(150px, 30vw)",
						height: "min(100px, 20vw)",
						border: "2px solid rgba(255,255,255,0.3)",
						borderRadius: 8,
						overflow: "hidden",
						background: "#000",
						boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
						zIndex: 3,
					}}
				>
					<video ref={setLocalVideoElement} data-testid="consultation-local-video" autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
					{voiceActive && (
						<div style={{ position: "absolute", bottom: 4, left: 4, padding: "2px 6px", background: "#52c41a", borderRadius: 4, display: "flex", alignItems: "center", gap: 4 }}>
							<AudioOutlined style={{ color: "#fff", fontSize: 10 }} />
						</div>
					)}
					<div style={{ position: "absolute", bottom: 4, right: 4, padding: "2px 6px", background: "rgba(0,0,0,0.6)", borderRadius: 4 }}>
						<Text style={{ color: "#fff", fontSize: 10 }}>You</Text>
					</div>
				</div>
			)}

				{/* Waiting room overlay — shown while patient waits to be admitted by practitioner */}
				{inWaitingRoom && !connecting && (
					<div
						data-testid="consultation-waiting-room"
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "rgba(0, 0, 0, 0.82)",
							backdropFilter: "blur(6px)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 5,
						}}
					>
						<Card
							className="rounded-2xl border-0"
							style={{ maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
							styles={{ body: { padding: "40px 32px", textAlign: "center" } }}
						>
							<Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 48, color: "var(--primary-color)" }} spin />} />
							<Title level={4} style={{ marginTop: 24, marginBottom: 8, color: "#1f2937" }}>
								You're in the waiting room
							</Title>
							<Text style={{ color: "#6b7280", fontSize: 14, display: "block", marginBottom: 20 }}>
								{waitingRoomMessage || "Your practitioner will admit you shortly. Please stay on this page."}
							</Text>
							<Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 20, fontSize: 13, padding: "4px 14px", lineHeight: "22px" }}>
								Your camera and microphone are ready
							</Tag>
						</Card>
					</div>
				)}

				{/* Waiting for doctor to join (after admitted but before consultation active) */}
				{waitingForDoctor && !inWaitingRoom && !connecting && videoReady && (
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "rgba(0, 0, 0, 0.82)",
							backdropFilter: "blur(6px)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 5,
						}}
					>
						<Card
							className="rounded-2xl border-0"
							style={{ maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
							styles={{ body: { padding: "40px 32px", textAlign: "center" } }}
						>
							<Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 48, color: "var(--primary-color)" }} spin />} />
							<Title level={4} style={{ marginTop: 24, marginBottom: 8, color: "#1f2937" }}>
								Waiting for your doctor to join…
							</Title>
							<Text style={{ color: "#6b7280", fontSize: 14, display: "block", marginBottom: 20 }}>
								Your video is ready. Please remain on this page.
							</Text>
							<Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 20, fontSize: 13, padding: "4px 14px", lineHeight: "22px" }}>
								You're connected and ready
							</Tag>
						</Card>
					</div>
				)}

				{/* Loading overlay */}
				{connecting && (
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "rgba(0, 0, 0, 0.95)",
							backdropFilter: "blur(8px)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 10,
						}}
					>
						<Card
							className="rounded-2xl border-0"
							style={{ maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
							styles={{ body: { padding: "40px 32px", textAlign: "center" } }}
						>
							<Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 56, color: "var(--primary-color)" }} spin />} />
							<Title level={4} style={{ marginTop: 24, marginBottom: 8, color: "#1f2937" }}>
								Connecting to consultation…
							</Title>
							<Text style={{ color: "#6b7280", fontSize: 14 }}>Setting up your camera and microphone</Text>
						</Card>
					</div>
				)}

				{/* Reconnecting overlay */}
				{reconnecting && !connecting && (
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "rgba(0, 0, 0, 0.65)",
							backdropFilter: "blur(4px)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 4,
						}}
					>
						<Card
							className="rounded-2xl border-0"
							style={{ maxWidth: 320, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
							styles={{ body: { padding: "32px 28px", textAlign: "center" } }}
						>
							<SyncOutlined spin style={{ fontSize: 40, color: "#faad14", marginBottom: 16 }} />
							<Title level={4} style={{ color: "#1f2937", margin: "0 0 8px 0" }}>
								Reconnecting…
							</Title>
							<Text style={{ color: "#6b7280", fontSize: 13 }}>Please stay on this page</Text>
						</Card>
					</div>
				)}

				{/* Thank You / Consultation Ended Screen */}
				{consultationEnded && (
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 15,
							padding: 40,
						}}
					>
						<Card
							className="rounded-2xl border-0"
							style={{ maxWidth: 500, width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
							styles={{ body: { padding: "52px 40px" } }}
						>
							<CheckCircleOutlined style={{ fontSize: 72, color: "#52c41a", marginBottom: 24 }} />
							<Title level={2} style={{ margin: "0 0 12px 0", color: "#1f2937" }}>
								Thank You!
							</Title>
							<Title level={4} style={{ margin: "0 0 20px 0", color: "#6b7280", fontWeight: "normal" }}>
								Your consultation has ended
							</Title>
							<Text style={{ fontSize: 15, color: "#6b7280", lineHeight: 1.7, display: "block", marginBottom: 32 }}>
								Your healthcare provider has ended the consultation.
								<br />
								Thank you for using our telehealth platform.
							</Text>
							<Button
								type="primary"
								size="large"
								style={{ height: 48, padding: "0 32px", fontSize: 16, borderRadius: 8 }}
								onClick={() => (window.location.href = "/")}
							>
								Return to Dashboard
							</Button>
						</Card>
					</div>
				)}

				{removed && (
					<div
						data-testid="consultation-removed"
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 15,
							padding: 40,
						}}
					>
						<Card
							className="rounded-2xl border-0"
							style={{ maxWidth: 500, width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
							styles={{ body: { padding: "52px 40px" } }}
						>
							<StopOutlined style={{ fontSize: 72, color: "#ff4d4f", marginBottom: 24 }} />
							<Title level={3} style={{ margin: "0 0 12px 0", color: "#1f2937" }}>
								You have been removed from this consultation
							</Title>
							<Text style={{ fontSize: 15, color: "#6b7280", lineHeight: 1.7, display: "block", marginBottom: 32 }}>
								Your practitioner has removed you from this call. If you think this is a mistake, please contact your healthcare provider.
							</Text>
							<Button
								type="primary"
								size="large"
								style={{ height: 48, padding: "0 32px", fontSize: 16, borderRadius: 8 }}
								onClick={() => (window.location.href = "/")}
							>
								Return to Dashboard
							</Button>
						</Card>
					</div>
				)}
			</div>

			{/* Chat launcher — bottom LEFT, clear of the self-view thumbnail, and above the ended overlay */}
			{isAdmitted && !chatOpen && (
				<div style={{ position: "absolute", left: 16, bottom: 16, zIndex: 20 }}>
					<Badge count={chatUnread} overflowCount={99} offset={[-4, 4]}>
						<Button
							shape="circle"
							size="large"
							icon={<MessageOutlined />}
							onClick={() => setChatOpen(true)}
							className="consult-chat-fab"
							data-testid="consultation-chat-toggle"
							aria-label={chatUnread > 0 ? `Open chat, ${chatUnread} unread` : "Open chat"}
							style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.5)" }}
						/>
					</Badge>
				</div>
			)}

			{/* Consultation Chat — mask off so the video stays visible AND clickable behind it */}
			<Drawer
				title="Chat"
				placement={isNarrow ? "bottom" : "right"}
				height={isNarrow ? "60%" : undefined}
				width={isNarrow ? undefined : 380}
				open={chatOpen}
				onClose={() => setChatOpen(false)}
				mask={false}
				styles={{ body: { padding: 0, display: "flex", flexDirection: "column", minHeight: 0 } }}
			>
				<div data-testid="consultation-chat-drawer" data-placement={isNarrow ? "bottom" : "right"} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
					<ConsultChatPanel
						messages={chatThread}
						currentRole="patient"
						onSend={sendChatMessage}
						onAttach={sendChatAttachment}
						disabled={consultationEnded}
						emptyText="No messages yet. Anything you send here goes to your practitioner."
						error={chatError}
						onDismissError={dismissChatError}
					/>
				</div>
			</Drawer>

			{/* Device Settings Drawer */}
			<Drawer
				title={
					<Space>
						<SettingOutlined style={{ color: "var(--primary-color)" }} />
						<span>Device Settings</span>
					</Space>
				}
				placement="right"
				open={deviceSettingsVisible}
				onClose={() => setDeviceSettingsVisible(false)}
				width={typeof window !== "undefined" && window.innerWidth < 480 ? "100%" : 400}
				styles={{ body: { padding: 20 } }}
				extra={
					<Button type="primary" size="small" style={{ borderRadius: 6 }} onClick={handleDeviceChange} loading={switchingDevices} disabled={switchingDevices} data-testid="consultation-device-apply">
						Apply Changes
					</Button>
				}
			>
				<Space direction="vertical" size={12} style={{ width: "100%" }}>
					{/* Camera Selection */}
					<Card
						size="small"
						className="rounded-xl border-0 shadow-sm"
						style={{ background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)" }}
					>
						<Text strong style={{ display: "block", marginBottom: 8, fontSize: 13, color: "#374151" }}>
							<VideoCameraOutlined style={{ marginRight: 6, color: "#1890ff" }} />
							Camera
						</Text>
						<Select
							value={selectedCamera}
							onChange={setSelectedCamera}
							data-testid="consultation-camera-select"
							style={{ width: "100%" }}
							options={cameras.map((cam) => ({
								label: cam.label || `Camera ${cam.deviceId.slice(0, 8)}...`,
								value: cam.deviceId,
							}))}
							placeholder="Select a Camera"
						/>
					</Card>

					{/* Microphone Selection */}
					<Card
						size="small"
						className="rounded-xl border-0 shadow-sm"
						style={{ background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)" }}
					>
						<Text strong style={{ display: "block", marginBottom: 8, fontSize: 13, color: "#374151" }}>
							<AudioOutlined style={{ marginRight: 6, color: "#52c41a" }} />
							Microphone
						</Text>
						<Select
							value={selectedMicrophone}
							onChange={setSelectedMicrophone}
							data-testid="consultation-microphone-select"
							style={{ width: "100%" }}
							options={microphones.map((mic) => ({
								label: mic.label || `Microphone ${mic.deviceId.slice(0, 8)}...`,
								value: mic.deviceId,
							}))}
							placeholder="Select a Microphone"
						/>
					</Card>

					{/* Info Section */}
					<Card
						size="small"
						className="rounded-xl border-0"
						style={{ background: "rgba(24, 144, 255, 0.06)", border: "1px solid rgba(24, 144, 255, 0.15)" }}
					>
						<Space direction="vertical" size={8} style={{ width: "100%" }}>
							<Space size={6}>
								<CheckCircleOutlined style={{ color: "var(--primary-color)", fontSize: 14 }} />
								<Text style={{ fontSize: 12, color: "var(--primary-color)", fontWeight: 500 }}>Changes will take effect immediately</Text>
							</Space>
							<Text style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
								If your video or audio isn't working, try selecting different devices or refreshing the page.
							</Text>
						</Space>
					</Card>
				</Space>
			</Drawer>
		</div>
		</>  
	);
};

export default PatientConsultation;
